import { useEffect, useRef } from 'react';
import { useProxyStore } from '../store/proxy-store';
import {
  useProjectStore,
  useTargetId,
  useTargetType,
} from '../store/projectStore';
import { mapBrowserMatchReplaceRuleToLocal } from '../utils/match-replace-rule.mapper';
import { deriveMatchReplaceEnabledFromRules } from '../utils/match-replace-rules-merge';
import {
  resolveMatchReplaceRulesForHydration,
  resolvePendingBrowserConfigHydration,
} from '../utils/match-replace-rules-hydration';
import { buildServerRulesSyncKey } from '../utils/match-replace-persistence';
import { formatTargetScopeKey, TARGET_TYPE } from '../utils/target-scope.utils';

export function useMatchReplaceRulesHydration() {
  const selectedTarget = useProjectStore((state) => state.selectedTarget);
  const targetId = useTargetId();
  const targetType = useTargetType();
  const currentTargetId = useProxyStore((state) => state.currentTargetId);
  const replaceMatchReplaceRulesForApplication = useProxyStore(
    (state) => state.replaceMatchReplaceRulesForApplication,
  );
  const mergeMatchReplaceRulesFromServer = useProxyStore(
    (state) => state.mergeMatchReplaceRulesFromServer,
  );
  const applyMatchReplaceEnabledForTarget = useProxyStore(
    (state) => state.applyMatchReplaceEnabledForTarget,
  );

  const previousApplicationIdRef = useRef<string | null>(null);
  const lastMergedServerRulesKeyRef = useRef<string | null>(null);

  const serverBrowserConfig = selectedTarget?.browserConfig;
  const resolvedTargetId =
    selectedTarget?.id || targetId || currentTargetId || null;
  const resolvedTargetType =
    selectedTarget?.targetType ?? targetType ?? TARGET_TYPE.WEB_APPLICATION;
  const targetScopeKey = resolvedTargetId
    ? formatTargetScopeKey({
        targetId: resolvedTargetId,
        targetType: resolvedTargetType,
      })
    : null;

  useEffect(() => {
    if (!selectedTarget?.id || !targetScopeKey) {
      if (previousApplicationIdRef.current !== null) {
        previousApplicationIdRef.current = null;
        lastMergedServerRulesKeyRef.current = null;
        replaceMatchReplaceRulesForApplication([]);
      }
      return;
    }

    const applicationId = selectedTarget.id;
    const isAppSwitch = previousApplicationIdRef.current !== applicationId;

    if (serverBrowserConfig === undefined) {
      const pending = resolvePendingBrowserConfigHydration({ isAppSwitch });
      if (pending.action === 'clear-rules-and-wait') {
        lastMergedServerRulesKeyRef.current = null;
        replaceMatchReplaceRulesForApplication([]);
      }
      return;
    }

    const serverRules = (serverBrowserConfig?.matchReplaceRules ?? []).map(
      mapBrowserMatchReplaceRuleToLocal,
    );
    const serverRulesKey = buildServerRulesSyncKey({
      applicationId,
      rules: serverRules,
    });

    if (isAppSwitch) {
      previousApplicationIdRef.current = applicationId;
      lastMergedServerRulesKeyRef.current = serverRulesKey;

      const storedEnabled =
        useProxyStore.getState().matchReplaceEnabledByTarget[targetScopeKey];
      const resolvedEnabled =
        storedEnabled ?? deriveMatchReplaceEnabledFromRules(serverRules);
      const dirtyRuleIds = new Set(
        useProxyStore.getState().matchReplaceDirtyRuleIds,
      );
      const localRules =
        useProxyStore.getState().settings.matchReplaceRules ?? [];
      const hydration = resolveMatchReplaceRulesForHydration({
        isAppSwitch: true,
        localRules,
        serverRules,
        dirtyRuleIds,
      });

      replaceMatchReplaceRulesForApplication(hydration.rules);
      applyMatchReplaceEnabledForTarget({
        targetScopeKey,
        enabled: resolvedEnabled,
      });
      return;
    }

    if (lastMergedServerRulesKeyRef.current === serverRulesKey) {
      return;
    }

    lastMergedServerRulesKeyRef.current = serverRulesKey;
    mergeMatchReplaceRulesFromServer(serverRules);
  }, [
    selectedTarget?.id,
    serverBrowserConfig,
    targetScopeKey,
    replaceMatchReplaceRulesForApplication,
    mergeMatchReplaceRulesFromServer,
    applyMatchReplaceEnabledForTarget,
  ]);
}
